<template>
  <div class="mb-10">
    <small
      v-if="errorMessage"
      class="block leading-tight text-coral mb-[4px]"
      v-text="errorMessage"
    />

    <div
      v-if="appliedDiscount && !errorMessage"
      class="mb-5 inline-flex items-center gap-5 rounded-full bg-blue px-10 py-2 text-white"
    >
      <small class="text-xs uppercase">{{ appliedDiscount.title }}</small>
      <button
        class="text-xs leading-none"
        @click="clearDiscount"
        :disabled="loading"
        aria-label="Remove discount"
      >
        ×
      </button>
    </div>

    <small
      v-if="noticeMessage && !errorMessage"
      class="mb-5 block leading-tight text-gray"
      v-text="noticeMessage"
    />

    <div class="flex">
      <input
        v-model="discountCode"
        type="text"
        autocomplete="on"
        placeholder="Discount Code"
        :disabled="loading"
        class="w-full appearance-none border border-gray bg-white px-10 py-5 text-sm leading-tight placeholder:text-gray focus:outline-none"
        @keyup.enter="setDiscount"
      />
      <button
        class="block cursor-pointer bg-blue px-15 py-5 text-center text-xs font-bold uppercase leading-tight text-white hover:bg-green"
        :disabled="loading || !discountCode"
        @click="setDiscount"
      >
        <span v-if="loading" class="cart-discount-loader" />
        <span v-else>Apply</span>
      </button>
    </div>
  </div>
</template>

<script>
import {mapState, mapActions} from "vuex";

// Shopify auto-generates gift card codes as 16 alphanumeric characters. A
// discount code can look identical, so this is only consulted after Shopify
// has already refused the code as a discount.
const GIFT_CARD_CODE = /^[a-z0-9]{16}$/i;

export default {
  name: "DiscountCode",
  data() {
    return {
      discountCode: "",
      errorMessage: "",
      giftCardCode: "",
      loading: false,
    };
  },
  computed: {
    ...mapState("cart", {
      cart: "cartData",
      pendingCode: "pendingDiscountCode",
    }),
    cartDiscount() {
      return this.cart?.cart_level_discount_applications?.[0];
    },
    appliedDiscount() {
      if (this.cartDiscount) return this.cartDiscount;
      return this.pendingCode ? {title: this.pendingCode} : null;
    },
    // Derived rather than stored, so it survives the remounts the cart page
    // forces via `:key="items.length"` and the store reset on every initCart.
    noticeMessage() {
      // Shopify redeems gift cards in checkout only: /discount/<code> and
      // /cart/update.js both handle discount codes exclusively, so the code
      // cannot be carried over from here. Point the buyer at the right field
      // instead of calling a valid card invalid.
      if (this.giftCardCode) {
        return "Gift cards are applied at checkout \u2014 enter this code in the \u201cDiscount code or gift card\u201d field there.";
      }

      if (this.cartDiscount || !this.pendingCode) return "";
      return "Applied at checkout.";
    },
  },
  watch: {
    "cart.total_price"(newVal, oldVal) {
      if (newVal === oldVal) return;
      this.errorMessage = "";
      if (this.discountCode) this.setDiscount();
    },
  },
  methods: {
    ...mapActions("cart", ["setCode", "applyDiscount", "clearDiscount"]),
    async setDiscount() {
      if (!this.discountCode) return;

      const code = this.discountCode.trim();

      this.errorMessage = "";
      this.giftCardCode = "";
      this.loading = true;

      try {
        await this.applyDiscount(code);

        const hasApplied =
          this.cart?.cart_level_discount_applications?.length > 0;
        const hasDiscountAmount = this.cart?.total_discount > 0;

        if (!hasApplied && !hasDiscountAmount) {
          // Shaped like a gift card, so it is not a broken discount code.
          if (GIFT_CARD_CODE.test(code)) {
            this.giftCardCode = code;
            this.discountCode = "";
            return;
          }

          this.errorMessage =
            "The discount code you entered is invalid or cannot be applied to the items in your cart.";
          return;
        }

        if (!hasApplied) {
          this.setCode(code);
        }

        localStorage.setItem("discountCode", code);
        this.discountCode = "";
      } catch {
        this.errorMessage = "Please enter a valid coupon code.";
      } finally {
        this.loading = false;
      }
    },
    async clearDiscount() {
      this.loading = true;
      this.errorMessage = "";
      this.giftCardCode = "";

      try {
        await this.$store.dispatch("cart/clearDiscount");
        localStorage.removeItem("discountCode");
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    // Runs before initCart resolves, so it must not depend on cartData.
    const savedDiscountCode = localStorage.getItem("discountCode");

    if (savedDiscountCode) this.setCode(savedDiscountCode);
  },
};
</script>

<style scoped>
.cart-discount-loader {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #fff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: cart-discount-spin 0.6s linear infinite;
}

@keyframes cart-discount-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
